import { Modal, Table, TableColumnsType, Button, Input, Space, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  ADDRESS_ALIAS_LIST,
  DELETE_ADDRESS_ALIAS,
  AddressAliasInfo,
  AddressAliasListResponse,
} from '../graphql/queries/getAddressAliasList';

interface AddressAliasListModalProps {
  visible: boolean;
  addressId: string | null;
  addressText?: string;
  onClose: () => void;
}

const AddressAliasListModal: React.FC<AddressAliasListModalProps> = ({
  visible,
  addressId,
  onClose,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // 输入框值与已应用的筛选值分离：输入不触发查询，点「查询」才应用
  const [aliasNameInput, setAliasNameInput] = useState('');
  const [fullAddressInput, setFullAddressInput] = useState('');
  const [appliedAliasName, setAppliedAliasName] = useState('');
  const [appliedFullAddress, setAppliedFullAddress] = useState('');

  const { data, loading, refetch } = useQuery<AddressAliasListResponse>(ADDRESS_ALIAS_LIST, {
    variables: {
      addressId: addressId ? Number(addressId) : undefined,
      aliasName: appliedAliasName || undefined,
      fullAddress: appliedFullAddress || undefined,
      page: currentPage,
      size: pageSize,
    },
    skip: !visible || !addressId,
    fetchPolicy: 'cache-and-network',
  });

  const [deleteAlias] = useMutation(DELETE_ADDRESS_ALIAS, {
    onCompleted: () => {
      message.success('删除成功');
      void refetch();
    },
    onError: (err) => {
      message.error(`删除失败：${err.message}`);
    },
  });

  // 弹窗关闭时重置分页与筛选，避免下次打开沿用上次状态
  useEffect(() => {
    if (!visible) {
      setCurrentPage(1);
      setPageSize(10);
      setAliasNameInput('');
      setFullAddressInput('');
      setAppliedAliasName('');
      setAppliedFullAddress('');
    }
  }, [visible]);

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = () => {
    setAppliedAliasName(aliasNameInput.trim());
    setAppliedFullAddress(fullAddressInput.trim());
    setCurrentPage(1);
  };

  const handleReset = () => {
    setAliasNameInput('');
    setFullAddressInput('');
    setAppliedAliasName('');
    setAppliedFullAddress('');
    setCurrentPage(1);
  };

  const showDeleteConfirm = (record: AddressAliasInfo) => {
    Modal.confirm({
      title: '确认删除',
      content: `确认删除别名「${record.aliasName}」？`,
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => deleteAlias({ variables: { id: Number(record.id) } }),
    });
  };

  const columns: TableColumnsType<AddressAliasInfo> = [
    {
      title: '别名',
      dataIndex: 'aliasName',
      key: 'aliasName',
      width: 220,
      ellipsis: true,
    },
    {
      title: '别名类型',
      dataIndex: 'aliasType',
      key: 'aliasType',
      width: 130,
      align: 'center',
    },
    {
      title: '别名完整地址',
      dataIndex: 'aliasFullAddress',
      key: 'aliasFullAddress',
      width: 320,
      ellipsis: true,
    },
    {
      title: '相似度',
      dataIndex: 'similarityScore',
      key: 'similarityScore',
      width: 100,
      align: 'center',
      render: (text: string | number | null) => (text ?? '-'),
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      align: 'center',
      render: (text: string | null) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center',
      render: (text: string | null) => (text ? new Date(text).toLocaleString() : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Button type="link" size="small" danger onClick={() => showDeleteConfirm(record)}>
          删除
        </Button>
      ),
    },
  ];

  const pageData = data?.addressAliasList;

  return (
    <Modal
      title="别名清单"
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={
        <Button onClick={onClose}>关闭</Button>
      }
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Space size={4}>
          <span>别名：</span>
          <Input
            placeholder="请输入别名"
            allowClear
            style={{ width: 220 }}
            value={aliasNameInput}
            onChange={(e) => setAliasNameInput(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Space>
        <Space size={4}>
          <span>地址：</span>
          <Input
            placeholder="请输入地址"
            allowClear
            style={{ width: 280 }}
            value={fullAddressInput}
            onChange={(e) => setFullAddressInput(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Space>
        <Button type="primary" onClick={handleSearch}>
          查询
        </Button>
        <Button onClick={handleReset}>重置</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={pageData?.content || []}
        loading={loading}
        rowKey="id"
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: pageData?.totalElements || 0,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: handleTableChange,
          onShowSizeChange: handleTableChange,
        }}
      />
    </Modal>
  );
};

export default AddressAliasListModal;
